import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AlTableColumn } from '../features/table/table.component';
import { RadarAxisInput, RadarTexts } from '../charts/basic/competency-radar/competency-radar.options';
import { SegmentOption } from '../segmented-control/segmented-control.component';
import {
  AxisView,
  buildView,
  CompetencyBehaviour,
  CompetencyLensKey,
  CompetencyProfile,
  CompetencyView,
  OVERALL,
  STATUS_ORDER,
} from './competency-profile.model';

type ChartMode = 'chart' | 'list';

/**
 * Kompetenzprofil (PRD competency-profile) for the lecturer: a course,
 * a lens (Lernbereiche · Bloom-Stufen · Handlungskompetenz nach DQR/KMK), the
 * radar (or the DQR matrix) with a reading aid, and every area as a
 * disclosure row with its objectives. Levels, never percentages.
 *
 * Everything the template iterates is built once per (profile, course, lens)
 * in `refresh()` — no getter hands `app-table` a fresh array (Regel 18).
 */
@Component({
  selector: 'app-competency-profile',
  templateUrl: './competency-profile.component.html',
  styleUrl: './competency-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompetencyProfileComponent implements OnChanges {
  @Input() profile: CompetencyProfile | null = null;
  @Input() loading = false;
  @Input() error = false;
  /** Course the page stands in — preselected when the profile has it. */
  @Input() defaultCourseId: string | null = null;
  @Output() retry = new EventEmitter<void>();

  readonly OVERALL = OVERALL;
  courseId = '';
  lens: CompetencyLensKey = 'areas';
  chartMode: ChartMode = 'chart';
  showCoverage = true;
  openKeys = new Set<string>();

  view: CompetencyView | null = null;
  radarAxes: RadarAxisInput[] = [];
  radarTexts: RadarTexts | null = null;
  courseOptions: { value: string; label: string }[] = [];
  lensOptions: SegmentOption[] = [];
  private baseLenses: SegmentOption[] = [];
  private goalLenses: SegmentOption[] = [];
  chartOptions: SegmentOption[] = [];
  columns: AlTableColumn[] = [];
  statusChips: { tone: string; text: string }[] = [];
  matrix: { group: string; title: string; cells: AxisView[] }[] = [];
  behaviour: CompetencyBehaviour | null = null;
  retiredText = '';
  withoutText = '';

  private readonly p = 'components.competency';

  constructor(private translate: TranslateService) {}

  ngOnChanges(): void {
    this.buildStatic();
    if (this.profile) {
      const ids = this.profile.courses.map((c) => c.course_id);
      const wanted = [this.courseId, this.defaultCourseId, this.profile.default_course_id].find(
        (id) => !!id && (ids.includes(id) || (id === OVERALL && !!this.profile?.overall)),
      );
      this.courseId = wanted || ids[0] || '';
      this.courseOptions = [
        ...(this.profile.overall ? [{ value: OVERALL, label: this.t('overall') }] : []),
        ...this.profile.courses.map((c) => ({ value: c.course_id, label: c.course_name || this.t('unknownCourse') })),
      ];
    }
    this.refresh();
  }

  selectCourse(id: string): void {
    if (!id || id === this.courseId) return;
    this.courseId = id;
    this.openKeys = new Set();
    this.refresh();
  }

  selectLens(lens: string): void {
    this.lens = lens as CompetencyLensKey;
    this.openKeys = new Set();
    this.refresh();
  }

  selectChartMode(mode: string): void {
    this.chartMode = mode as ChartMode;
  }

  toggleCoverage(on: boolean): void {
    this.showCoverage = on;
  }

  onToggle(key: string, event: Event): void {
    const open = (event.target as HTMLDetailsElement).open;
    const next = new Set(this.openKeys);
    if (open) next.add(key);
    else next.delete(key);
    this.openKeys = next;
  }

  openArea(key: string): void {
    this.openKeys = new Set(this.openKeys).add(key);
    setTimeout(() => {
      const el = document.getElementById(this.areaId(key));
      el?.scrollIntoView({ block: 'nearest', behavior: this.reducedMotion() ? 'auto' : 'smooth' });
      (el?.querySelector('summary') as HTMLElement | null)?.focus({ preventScroll: true });
    });
  }

  areaId(key: string): string {
    return `cp-area-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  }

  trackAxis(_i: number, a: AxisView): string {
    return a.key;
  }

  trackOption(_i: number, o: { value: string }): string {
    return o.value;
  }

  get showChart(): boolean {
    return !!this.view && this.chartMode === 'chart' && this.view.chart !== 'list';
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(`${this.p}.${key}`, params);
  }

  private reducedMotion(): boolean {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  private buildStatic(): void {
    if (this.lensOptions.length) return;
    this.baseLenses = (['areas', 'bloom', 'dqr'] as CompetencyLensKey[]).map((v) => ({
      value: v, label: this.t(`lenses.${v}`),
    }));
    // The customer's goals (PRD mastery-objectives M3) only where a course has them.
    this.goalLenses = [...this.baseLenses, { value: 'goals', label: this.t('lenses.goals') }];
    this.lensOptions = this.baseLenses;
    this.chartOptions = [
      { value: 'chart', label: this.t('views.chart') },
      { value: 'list', label: this.t('views.list') },
    ];
    const col = (key: string) => this.t(`columns.${key}`);
    this.columns = [
      { key: 'objective', title: col('objective') },
      { key: 'level', title: col('level') },
      { key: 'bloom', title: col('bloom') },
      { key: 'answers', title: col('answers') },
      { key: 'review', title: col('review'), kind: 'date' },
      { key: 'last', title: col('last'), kind: 'date' },
    ];
    this.radarTexts = {
      level: this.t('seriesLevel'),
      coverage: this.t('seriesCoverage'),
      rings: [this.t('levels.learning'), this.t('levels.proficient'), this.t('levels.mastered')],
    };
  }

  private refresh(): void {
    const shown = this.profile?.courses.find((c) => c.course_id === this.courseId);
    this.lensOptions = shown?.lenses.goals && this.goalLenses.length ? this.goalLenses : this.baseLenses;
    if (this.lens === 'goals' && !shown?.lenses.goals) this.lens = 'areas';
    const lang = this.translate.currentLang || 'de';
    this.view = this.profile?.courses.length
      ? buildView(this.profile, this.courseId, this.lens, (k, params) => this.translate.instant(k, params), lang)
      : null;
    const view = this.view;
    if (!view) {
      this.radarAxes = [];
      this.matrix = [];
      this.statusChips = [];
      this.behaviour = null;
      return;
    }
    this.radarAxes = view.axes.map((a) => ({
      key: a.key,
      name: a.label,
      level: a.level,
      coverage: a.coverage,
      muted: a.state === 'not_captured' || a.state === 'open',
      levelLabel: a.levelLabel,
    }));
    const tone = { strong: 'accept', building: 'info', refresh: 'warning', open: 'neutral' } as const;
    this.statusChips = STATUS_ORDER.filter((s) => view.summary.status?.[s]).map((s) => ({
      tone: tone[s], text: `${view.summary.status[s]} ${this.t(`status.${s}`)}`,
    }));
    if (view.summary.misconceptions) {
      this.statusChips.push({ tone: 'error', text: this.t('misconceptionChip', { count: view.summary.misconceptions }) });
    }
    this.matrix = view.lens === 'dqr' && view.courseId !== OVERALL
      ? ['professional', 'personal'].map((group) => ({
          group,
          title: this.t(`dqrGroups.${group}`),
          cells: view.axes.filter((a) => a.group === group),
        }))
      : [];
    const course = this.profile!.courses.find((c) => c.course_id === view.courseId);
    this.behaviour = this.profile!.audience === 'lecturer'
      ? (this.courseId === OVERALL ? this.profile!.behaviour : course?.behaviour) || null
      : null;
    this.retiredText = course?.retired?.length ? this.t('retired', { count: course.retired.length }) : '';
    const without = this.profile!.courses_without_objectives || [];
    this.withoutText = this.courseId === OVERALL && without.length
      ? this.t('coursesWithout', { names: without.map((c) => c.course_name || '—').join(', ') })
      : '';
  }
}
